import { SearchFormModel } from "../components/SearchForm";
import { Fsm, FsmBuilder } from 'little-fsm';
import { homeStateSubFsmBuilder, HomeStateFsmScheme } from "./home/HomeFsmScheme";
import { seatSelectionSubFsmBuilder, SeatReservationFsmScheme } from "./seatreservation/SeatReservationFsmScheme";
import { tripSearchSubFsmBuilder, TripSearchFsmScheme } from "./trip-search/TripSearchFsmScheme";
import { tentativeBookingCreationSubFsmBuilder, TentativeBookingCreationFsmScheme } from './tentative-booking/TentativeBookingCreationFsmScheme'
import { TripSearchFsmContext } from "./trip-search/TripSearchFsmContext";
import { SeatReservationFsmContext } from "./seatreservation/SeatReservationFsmContext";
import { HomeFsmContext } from "./home/HomeFsmContext";
import { TentativeBookingCreationFsmContext } from "./tentative-booking/TentativeBookingCreationFsmContext";
import { PaymentAttemptFsmContext } from "./payment-attempt/PaymentAttemptFsmContext";
import { PaymentAttemptFsmScheme, paymentAttemptSubFsmBuilder } from "./payment-attempt/PaymentAttemptFsmScheme";
import { BookingConfirmationFsmScheme, bookingConfirmationSubFsmBuilder } from "./booking-confirmation/BookingConfirmationFsmScheme";
import { BookingConfirmationFsmContext } from "./booking-confirmation/BookingConfirmationFsmContext";
import BillingBreakdown from "../models/BillingBreakdown";
import { TentativeBookingReviewFsmContext } from "./review/TentativeBookingReviewFsmContext";
import { PaymentSelectionFsmScheme, paymentSelectionSubFsmBuilder } from "./payment-selection/PaymentSelectionFsmScheme";
import { PaymentSelectionFsmContext } from "./payment-selection/PaymentSelectionFsmContext";
import { TentativeBookingReviewFsmScheme, tentativeBookingReviewSubFsmBuilder } from "./review/TentativeBookingReviewFsmScheme";
import { PaymentCompletedFsmScheme, paymentCompleteSubFsmBuilder } from "./payment-complete/PaymentCompleteFsmScheme";
import { TravellerDTO } from "../apiclient/ApiDtos";
import { WayPointSelectionFsmScheme, wayPointSelectionSubFsmBuilder } from "./pickup-dropoff-order/WayPointsSelectionFsmScheme";
import { WayPointSelectionFsmContext } from "./pickup-dropoff-order/WayPointsSelectionFsmContext";
import { TripSelectedFsmScheme } from "./trip-selected/TripSelectedFsmScheme";
import { TripSelectedFsmContext } from "./trip-selected/TripSelectedFsmContext";

export type FetchingStatus = "fetching" | "success" | "error"
export type SubmissionStatus = "submitting" | "success" | "error"
export type WaitingStatus = "waiting" | "timeout"

export type AppChannel = "pwa" | "miniapp";

export type RootFsmScheme = {
    states: {
        'home': HomeStateFsmScheme,
        "search": TripSearchFsmScheme,
        "choice_after_trip_selected": TripSelectedFsmScheme,
        "seat_reservation": SeatReservationFsmScheme,
        "choice_after_seat_reservation": SeatReservationFsmScheme,
        "pickup_dropoff_selection": WayPointSelectionFsmScheme,
        "tentative_booking_creation": TentativeBookingCreationFsmScheme,
        "tentative_booking_creation_termination_decision": TentativeBookingCreationFsmScheme,
        "review": TentativeBookingReviewFsmScheme,
        "payment_attempt": PaymentAttemptFsmScheme,
        "booking_confirmation": BookingConfirmationFsmScheme,
        "payment_selection": PaymentSelectionFsmScheme,
        "payment_duplicate": PaymentCompletedFsmScheme
    },
    events: {
        "back": {}
    }
}

export function rootMobileAppFsmBuilder(channel: AppChannel): Fsm<RootFsmScheme> {
    let builder = new FsmBuilder<RootFsmScheme>();

    builder.compositeState("home", homeStateSubFsmBuilder())
        .transitionOnCompletion('search', (homeContext) => {
            return new TripSearchFsmContext(
                homeContext.searchFormModel,
                homeContext.searchFormModel.toSearchCriteria(homeContext.isSearchingSmallCar)
            );
        })

    builder.compositeState("search", tripSearchSubFsmBuilder())
        .transitionOnCompletion('choice_after_trip_selected', c => {
            return new TripSelectedFsmContext(c, c.selectedTripSearchResult!)
        })
        .transitionOnTermination('home', (c) => {
            return new HomeFsmContext(c.searchFormModel, c.searchCriteria.isSmallCar)
        })

    builder.choiceState('choice_after_trip_selected')
        .transition(c =>
        (
            c.selectedTrip.isSeatSelectionAllowed
            || (
                !c.selectedTrip.isSeatSelectionAllowed &&
                !c.selectedTrip.hasPickUpService &&
                !c.selectedTrip.hasDropOffService
            )
        )//need to show (seat assignment auto or seatselection)
            , 'seat_reservation', (c) => {
                return new SeatReservationFsmContext(c, null, []);

            },
            "need to show seat assignment info(seatplan or auto)"
        )
        .transition(c => (!c.selectedTrip!.isSeatSelectionAllowed), 'choice_after_seat_reservation', (c) => {
            return new SeatReservationFsmContext(c, null, [])
        }, "no need to show seat assignment info")


    builder.compositeState("seat_reservation", seatSelectionSubFsmBuilder())
        .transitionOnCompletion('choice_after_seat_reservation', c => c)
        .transitionOnTermination('search', (seatReservationFsmContext) => {
            seatReservationFsmContext = seatReservationFsmContext.cancelTripCustomization();
            return seatReservationFsmContext.tripSelectedContext.tripSearchContext
        }
        )

    builder.choiceState('choice_after_seat_reservation')
        .transition(c => c.tripSelectedContext.selectedTrip.isSeatSelectionAllowed && (c.tripSelectedContext.selectedTrip.hasPickUpService || c.tripSelectedContext.selectedTrip.hasDropOffService), 'pickup_dropoff_selection', (c) => {
            return new WayPointSelectionFsmContext(c.tripSelectedContext, c.tripDetails, c.selectedSeats, [], [], c.tripCustomization!!);
        }, "has pickup/dropoff service")
        .transition(c => (!c.tripSelectedContext.selectedTrip.hasPickUpService && !c.tripSelectedContext.selectedTrip.hasDropOffService), 'tentative_booking_creation', (c) => {
            const travellerInfo: TravellerDTO = {
                name: "",
                email: "",
                phoneNumber: "",
                gender: 'MALE_ONLY',
                specialRequest: ""
            };
            return new TentativeBookingCreationFsmContext(
                new WayPointSelectionFsmContext(c.tripSelectedContext, c.tripDetails, c.selectedSeats, [], [], c.tripCustomization!!),
                travellerInfo);
        }, 'no pickup/dropoff service')

    builder.compositeState("pickup_dropoff_selection", wayPointSelectionSubFsmBuilder())
        .transitionOnCompletion('tentative_booking_creation', (c) => {
            const travellerInfo: TravellerDTO = {
                name: "",
                email: "",
                phoneNumber: "",
                gender: 'MALE_ONLY',
                specialRequest: ""
            };
            return new TentativeBookingCreationFsmContext(
                new WayPointSelectionFsmContext(c.tripSelectedContext, c.tripDetails, c.selectedSeats, c.addedPickupLineItems, c.addedDropOffLineItems, c.tripCustomization),
                travellerInfo);
        })
        .transitionOnTermination('seat_reservation', (context) => {
            return new SeatReservationFsmContext(
                context.tripSelectedContext,
                context.tripDetails,
                context.selectedSeats,
                null,
                context.tripCustomization
            )
        })

    builder.compositeState("tentative_booking_creation", tentativeBookingCreationSubFsmBuilder())
        .transitionOnCompletion('review', (c) => {
            return new TentativeBookingReviewFsmContext(
                c.tripCustomizationContext,
                c.travellerInfo,
                c.tentativeBooking!
            )
        })
        .transitionOnTermination('tentative_booking_creation_termination_decision', context => context);



    builder.choiceState('tentative_booking_creation_termination_decision')
        .transition(c => (c.tripCustomizationContext instanceof WayPointSelectionFsmContext && c.tripCustomizationContext.hasPickUpDropOffOrder()), 'pickup_dropoff_selection', (c) => {
            return c.tripCustomizationContext as WayPointSelectionFsmContext;
        }, "back to pickup dropoff")
        .transition(c => (!c.tripCustomizationContext.tripDetails!!.isSeatSelectionAllowed), 'seat_reservation', (c) => {
            return new SeatReservationFsmContext(
                c.tripCustomizationContext.tripSelectedContext,
                c.tripCustomizationContext.tripDetails,
                c.tripCustomizationContext.selectedSeats,
                null,
                c.tripCustomizationContext.tripCustomization
            );
        }, "back to seat-auto info page")
        .transition(c => !(c.tripCustomizationContext instanceof WayPointSelectionFsmContext && c.tripCustomizationContext.hasPickUpDropOffOrder()), 'seat_reservation', (c) => {
            return new SeatReservationFsmContext(
                c.tripCustomizationContext.tripSelectedContext,
                c.tripCustomizationContext.tripDetails,
                c.tripCustomizationContext.selectedSeats,
                null,
                c.tripCustomizationContext.tripCustomization
            );
        }, 'back to seat selection');

    if (channel == 'pwa') {
        builder.compositeState('review', tentativeBookingReviewSubFsmBuilder())
            .transitionOnCompletion('payment_selection', (c) => {
                return new PaymentSelectionFsmContext(
                    c,
                    true,
                    null,
                    null
                )
            })
            .transitionOnTermination('search', (tentativeBookingCreationContext) => {
                return tentativeBookingCreationContext.tripCustomizationContext.tripSelectedContext.tripSearchContext
            });

        builder.compositeState('payment_selection', paymentSelectionSubFsmBuilder())
            .transitionOnCompletion('payment_duplicate', (c) => c)
            .transitionOnTermination('search', (c) => {
                return c.tentativeBookingReviewContext.tripCustomizationContext.tripSelectedContext.tripSearchContext
            })

        builder.compositeState('payment_duplicate', paymentCompleteSubFsmBuilder())
            .transitionOnCompletion('home', (c) => {
                return new HomeFsmContext(new SearchFormModel(null, null, null, 1, null), false);
            })
            .transitionOnTermination('home', (c) => {
                return new HomeFsmContext(new SearchFormModel(null, null, null, 1, null), false)
            })

    } else {//miniapp
        builder.compositeState('review', tentativeBookingReviewSubFsmBuilder())
            .transitionOnCompletion('payment_attempt', (tentativeBookingCreationContext) => {
                let tentativeBooking = tentativeBookingCreationContext.tentativeBooking!;
                return new PaymentAttemptFsmContext(
                    tentativeBookingCreationContext.tripCustomizationContext.tripSelectedContext.tripSearchContext,
                    tentativeBooking,
                    new BillingBreakdown(tentativeBooking.subtotal),
                    null,
                    null,
                    false
                );
            })
            .transitionOnTermination('search', (tentativeBookingCreationContext) => {
                return tentativeBookingCreationContext.tripCustomizationContext.tripSelectedContext.tripSearchContext
            });


        builder.compositeState('payment_attempt', paymentAttemptSubFsmBuilder())
            .transitionOnTermination('search', (c) => {
                return c.tripSearchContext
            })
            .transitionOnCompletion('booking_confirmation', (c) => new BookingConfirmationFsmContext(c.tenativeBooking));
    }




    builder.compositeState('booking_confirmation', bookingConfirmationSubFsmBuilder())
        .transitionOnCompletion('home', (c) => new HomeFsmContext(new SearchFormModel(null, null, null, 1, 'Local'), false))
        .transitionOnTermination('home', (c) => new HomeFsmContext(new SearchFormModel(null, null, null, 1, 'Local'), false))

    return builder.build();
}